var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Box, Button, ButtonGroup, useMediaQuery, IconButton, InputAdornment, Skeleton, } from "@mui/material";
import FDialog from "@ui/components/dialog/FDialog";
import { FInput } from "@ui/components/inputs";
import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { stack2 } from "@ui/components/style/style";
import { Icon } from "@iconify/react";
import { useCreateForecastAdjustmentMutation, useGetForecastAdjustmentQuery, useUpdateForecastAdjustmentMutation, } from "../../../features/projects/projectsApi";
// import { RefreshProjectStatusesProject, useRefreshProjectStatusesMutation } from "../../../features/projectStatuses/projectStatusesApi";
import { useAuth } from "../../../auth/useAuth";
import { useSnackbar } from "notistack";
export const ForcastAdjustDialog = ({ open, onClose, parentProjectNumber, childProjectNumber, projectId, companyId, isSaveDisabled, }) => {
    const isMobile = useMediaQuery("(max-width:800px)");
    const { account } = useAuth();
    const [createForecastAdjustment, { isLoading: isCreating }] = useCreateForecastAdjustmentMutation();
    const [updateForecastAdjustment, { isLoading: isUpdating }] = useUpdateForecastAdjustmentMutation();
    // const [refreshProjectStatuses] = useRefreshProjectStatusesMutation();
    const { enqueueSnackbar } = useSnackbar();
    const [submitErrorLines, setSubmitErrorLines] = React.useState(null);
    const isLoading = isCreating || isUpdating;
    const formatGuid = (guid) => {
        const cleaned = guid.replace(/[-\s]/g, "");
        if (!/^[0-9a-f]{32}$/i.test(cleaned)) {
            return guid;
        }
        return `${cleaned.slice(0, 8)}-${cleaned.slice(8, 12)}-${cleaned.slice(12, 16)}-${cleaned.slice(16, 20)}-${cleaned.slice(20, 32)}`;
    };
    // Fetch existing forecast adjustment data
    const { data: forecastData, isLoading: isFetchingData, isFetching, isSuccess, isError, fulfilledTimeStamp, refetch, } = useGetForecastAdjustmentQuery({
        parentProjectNumber,
        childProjectNumber,
        companyId,
    }, {
        skip: !open || !parentProjectNumber || !childProjectNumber || !companyId,
        refetchOnMountOrArgChange: true,
    });
    const { control, handleSubmit, watch, reset, formState: { errors }, } = useForm({
        defaultValues: {
            PocOverrule: "",
            Reden: "",
            tabSelect: "seriematig",
        },
        mode: "onChange",
        reValidateMode: "onChange",
    });
    const pocOverruleValue = watch("PocOverrule");
    const handleIncrementPoc = () => {
        const currentValue = pocOverruleValue
            ? Number.parseFloat(pocOverruleValue)
            : 0;
        const newValue = Math.min(100, currentValue + 1);
        reset(Object.assign(Object.assign({}, watch()), { PocOverrule: String(newValue) }));
    };
    const handleDecrementPoc = () => {
        const currentValue = pocOverruleValue
            ? Number.parseFloat(pocOverruleValue)
            : 0;
        const newValue = Math.max(1, currentValue - 1);
        reset(Object.assign(Object.assign({}, watch()), { PocOverrule: String(newValue) }));
    };
    const resetToDefaults = React.useCallback(() => {
        reset({
            PocOverrule: "",
            Reden: "",
            tabSelect: "",
        });
    }, [reset]);
    // Used to ensure we only populate the form from a *fresh* fetch after opening
    const [openedAt, setOpenedAt] = React.useState(0);
    // Always clear form on close; also clear immediately on open and force a refetch
    useEffect(() => {
        if (!open) {
            resetToDefaults();
            setSubmitErrorLines(null);
            return;
        }
        // dialog opened
        const now = Date.now();
        setOpenedAt(now);
        resetToDefaults();
        setSubmitErrorLines(null);
        if (parentProjectNumber && childProjectNumber) {
            // Force a request even if RTKQ has cached data
            refetch();
        }
    }, [open, parentProjectNumber, childProjectNumber, refetch, resetToDefaults]);
    // Populate only when the request completes after this open-cycle
    useEffect(() => {
        if (!open)
            return;
        // If the backend returns 404/204/etc, keep cleared
        if (isError) {
            resetToDefaults();
            return;
        }
        if (!isSuccess)
            return;
        if (isFetching || isFetchingData)
            return;
        if (!fulfilledTimeStamp || fulfilledTimeStamp < openedAt)
            return;
        if (forecastData === null || forecastData === void 0 ? void 0 : forecastData.id) {
            reset({
                PocOverrule: forecastData.pocOverrule !== null
                    ? String(forecastData.pocOverrule)
                    : "",
                Reden: forecastData.comment || "",
                tabSelect: forecastData.adjustmentCategory || "",
            });
        }
        else {
            resetToDefaults();
        }
    }, [
        open,
        isError,
        isSuccess,
        isFetching,
        isFetchingData,
        fulfilledTimeStamp,
        openedAt,
        forecastData,
        reset,
        resetToDefaults,
    ]);
    const tabOptions = [
    // { value: "seriematig", label: "Seriematig bouwen" },
    // { value: "materiaal", label: "Materiaal op voorraad" },
    // { value: "overig", label: "Overig" },
    ];
    // Show loader while fetching initial data
    const isLoadingData = isFetchingData || (open && !fulfilledTimeStamp);
    return (React.createElement(FDialog, { isOpen: open, onClose: onClose, onSubmit: () => {
            if (isSaveDisabled) {
                enqueueSnackbar("Opslaan is uitgeschakeld: geen configuratiemaand voor dit bedrijf", { variant: "warning" });
                return;
            }
            const submit = handleSubmit((values) => __awaiter(void 0, void 0, void 0, function* () {
                try {
                    const pocOverrule = values.PocOverrule.trim() === ""
                        ? null
                        : Number.parseFloat(values.PocOverrule);
                    const comment = values.Reden.trim() === "" ? null : values.Reden;
                    // Format GUID to include hyphens (URL route has it without hyphens)
                    const formattedProjectId = formatGuid(projectId.trim());
                    if (forecastData === null || forecastData === void 0 ? void 0 : forecastData.id) {
                        // Update existing forecast adjustment
                        yield updateForecastAdjustment({
                            id: forecastData.id,
                            childProjectNumber: childProjectNumber,
                            pocOverrule: pocOverrule !== null && Number.isFinite(pocOverrule)
                                ? pocOverrule
                                : null,
                            adjustmentCategory: values.tabSelect,
                            comment,
                            modifiedBy: (account === null || account === void 0 ? void 0 : account.username) || (account === null || account === void 0 ? void 0 : account.name) || "",
                            companyId: companyId,
                            isActive: true,
                            isDeleted: false,
                        }).unwrap();
                        enqueueSnackbar("Forecast aanpassing succesvol bijgewerkt", {
                            variant: "success",
                        });
                    }
                    else {
                        // Create new forecast adjustment
                        yield createForecastAdjustment({
                            projectNumber: parentProjectNumber,
                            projectId: formattedProjectId,
                            childProjectNumber: childProjectNumber,
                            pocOverrule: pocOverrule !== null && Number.isFinite(pocOverrule)
                                ? pocOverrule
                                : null,
                            adjustmentCategory: values.tabSelect,
                            comment,
                            createdBy: (account === null || account === void 0 ? void 0 : account.username) || (account === null || account === void 0 ? void 0 : account.name) || "",
                            companyId: companyId,
                        }).unwrap();
                        enqueueSnackbar("Forecast aanpassing succesvol aangemaakt", {
                            variant: "success",
                        });
                    }
                    onClose();
                }
                catch (err) {
                    enqueueSnackbar("Fout bij het opslaan van forecast aanpassing", {
                        variant: "error",
                    });
                }
            }));
            void submit();
        }, loading: isLoading, title: "Waarom is de POC aangepast?", cancelText: "annuleren", confirmText: "Cijfers opslaan" }, isLoadingData ? (React.createElement(Box, { sx: Object.assign({}, stack2) },
        React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { borderRadius: 1 } }),
        React.createElement(Skeleton, { variant: "rectangular", height: 40, sx: { borderRadius: 1 } }),
        React.createElement(Skeleton, { variant: "rectangular", height: 100, sx: { borderRadius: 1 } }))) : (React.createElement(Box, { sx: Object.assign({}, stack2) },
        React.createElement(Box, { sx: { position: "relative" } },
            React.createElement(FInput, { control: control, name: "PocOverrule", label: "POC overrule", type: "number", error: errors.PocOverrule, rules: {
                    required: "POC overrule is verplicht",
                    validate: (value) => {
                        const num = Number.parseFloat(value);
                        if (!Number.isFinite(num)) {
                            return "Voer een geldig getal in";
                        }
                        if (num < 1 || num > 100) {
                            return "POC overrule moet tussen 1 en 100 liggen";
                        }
                        return true;
                    },
                }, InputProps: {
                    endAdornment: (React.createElement(InputAdornment, { position: "end" },
                        React.createElement(Box, { sx: { display: "flex", flexDirection: "column", gap: 0 } },
                            React.createElement(IconButton, { size: "small", onClick: handleIncrementPoc, disabled: Number.parseFloat(pocOverruleValue || "0") >= 100, sx: { padding: "2px" } },
                                React.createElement(Icon, { icon: "eva:arrow-up-outline", width: "16", height: "16" })),
                            React.createElement(IconButton, { size: "small", onClick: handleDecrementPoc, disabled: Number.parseFloat(pocOverruleValue || "0") <= 1, sx: { padding: "2px" } },
                                React.createElement(Icon, { icon: "eva:arrow-down-outline", width: "16", height: "16" }))))),
                } })),
        React.createElement(Controller, { name: "tabSelect", control: control, defaultValue: "seriematig", render: ({ field }) => (React.createElement(Box, null,
                React.createElement(ButtonGroup, { variant: "outlined", size: isMobile ? "small" : "medium", color: "inherit" }, tabOptions.map((tab) => (React.createElement(Button, { key: tab.value, color: "inherit", sx: { fontSize: isMobile ? "11px" : "" }, size: isMobile ? "small" : "medium", variant: field.value === tab.value ? "contained" : "outlined", onClick: () => field.onChange(tab.value), className: field.value === tab.value ? "selected" : "", disableRipple: true }, tab.label)))))) }),
        React.createElement(FInput, { control: control, name: "Reden", label: "Reden", multiline: true, rows: 3, error: errors.Reden, rules: {
                validate: (value) => {
                    return true;
                },
            } })))));
};
